import type { CampaignDto } from '@/generated/korza-api-client/types.gen';
import type { ChartGranularity } from '@/lib/chart-axis';
import type { HomeChartRow } from '@/lib/home-metrics';
import type { PartialBuckets } from '@/lib/hooks/use-partial-buckets';
import type { PartialRow } from '@/lib/partial-buckets';
import { MetricsAreaChart } from '@/components/ui/charts/area-chart';
import { MetricsChartAreaSeries } from '@/components/ui/charts/chart-area-series';
import { ChartCampaignMarkers } from '@/components/ui/charts/chart-campaign-markers';

export type HomeRevenueChartRow = PartialRow<HomeChartRow, 'revenue'>;

export interface HomeRevenueChartProps {
  /** Rows after `markPartialRows(rows, 'revenue', partial.edges)`. */
  rows: HomeRevenueChartRow[];
  /** compact: no Y axis (mobile, 120px box); full: axes (desktop, 320px box). */
  variant: 'compact' | 'full';
  granularity: ChartGranularity;
  partial: PartialBuckets;
  /** Campaigns of the period by bucket (`groupCampaignsByBucket`). */
  groups: Map<string, CampaignDto[]>;
  onActiveIndexChange: (index: number | null) => void;
  /** A campaign marker was clicked, tapped or focused (row index). */
  onMarkerActivate: (index: number) => void;
}

/**
 * Início revenue chart: the partial bucket dashed with its label (CHART-02,
 * CHART-03) and a marker on each bucket with campaigns, labelled on desktop
 * (HOME-04). The card's headline is the reading, so the tooltip renders
 * nothing (cursor and active dot stay).
 */
export function HomeRevenueChart({
  rows,
  variant,
  granularity,
  partial,
  groups,
  onActiveIndexChange,
  onMarkerActivate,
}: HomeRevenueChartProps) {
  return (
    <div className="h-full w-full">
      <MetricsAreaChart
        data={rows}
        className="min-h-0"
        hideYAxis={variant === 'compact'}
        tooltipContent={() => null}
        onActiveIndexChange={onActiveIndexChange}
        partialLegend={partial.edges}
      >
        <MetricsChartAreaSeries
          dataKey="revenue"
          partialDataKey="revenuePartial"
          partialLabels={partial.labels}
        />
        <ChartCampaignMarkers
          data={rows}
          groups={groups}
          granularity={granularity}
          valueKey="revenue"
          labels={variant === 'full'}
          onMarkerActivate={onMarkerActivate}
        />
      </MetricsAreaChart>
    </div>
  );
}
